import {
  api,
  AvailableSpeakingCertificationSet,
  CertificationBasicDataResult,
  PagedResultOfSpeakingCertificationListItemResult,
  SpeakingCertificationCalculation,
  SpeakingCertificationCalculationDetails,
  SpeakingCertificationCalculationResult,
  SpeakingCertificationDetailsResult,
  SpeakingCertificationRate,
} from 'api';
import { rtkQueryApi } from 'store/rtkQueryApi';

export const speakingCertificationApi = rtkQueryApi.injectEndpoints({
  endpoints: (builder) => ({
    speakingCertificationList: builder.query<
      PagedResultOfSpeakingCertificationListItemResult,
      { examinerId: number; page: number; count: number }
    >({
      query: (params) => api.speakingCertification.getExaminerSpeakingCertifications(params),
      providesTags: ['SpeakingCertificationList'],
    }),

    speakingCertificationDetails: builder.query<
      SpeakingCertificationDetailsResult,
      { examinerId: number; id: number }
    >({
      query: ({ examinerId, id }) => api.speakingCertification.getDetails({ examinerId, id }),
      providesTags: ['SpeakingCertification'],
    }),

    speakingNextCertDate: builder.query<string | null, { examinerId: number }>({
      query: ({ examinerId }) =>
        api.speakingCertification.getNextCertificationsDate({ examinerId }),
      providesTags: ['SpeakingCertificationList'],
    }),

    speakingCertRates: builder.query<SpeakingCertificationRate[], { certificationId: number }>({
      query: ({ certificationId }) =>
        api.speakingCertificationRate.getExaminerSpeakingCertifications({ certificationId }),
      providesTags: ['SpeakingCertification'],
    }),

    availableSpeakingSets: builder.query<
      AvailableSpeakingCertificationSet[],
      { examinerId: number }
    >({
      query: ({ examinerId }) =>
        api.speakingCertificationSet.getExaminerSpeakingCertifications({ examinerId }),
      providesTags: ['SpeakingCertification'],
    }),

    speakingIsAttemptsExceeded: builder.query<boolean, { examinerId: number; setId: number }>({
      query: ({ examinerId, setId }) =>
        api.speakingCertification.isNumberOfAttemptsExceeded({ examinerId, setId }),
      providesTags: ['SpeakingCertification'],
    }),

    speakingIsGapExceeded: builder.query<
      boolean,
      { examinerId: number; setId: number; certificationDate?: string | null }
    >({
      query: ({ examinerId, setId, certificationDate }) =>
        api.speakingCertification.isGapBetweenAttemptsExceeded({
          examinerId,
          setId,
          certificationDate,
        }),
      providesTags: ['SpeakingCertification'],
    }),

    speakingCertIsDeletable: builder.query<
      boolean,
      { certificationId: number; examinerId: number }
    >({
      query: ({ examinerId, certificationId }) =>
        api.speakingCertification.checkIfCertificationIsDeletable({ examinerId, certificationId }),
      providesTags: ['SpeakingCertification'],
    }),

    speakingLastPassedCertDate: builder.query<CertificationBasicDataResult, { examinerId: number }>(
      {
        query: ({ examinerId }) =>
          api.speakingCertification.getLastPassedCertificationBasicData({ examinerId }),
        providesTags: ['SpeakingCertificationList'],
      }
    ),

    calculateSpeakingCert: builder.mutation<
      SpeakingCertificationCalculationResult,
      { examinerId: number; data: SpeakingCertificationCalculation }
    >({
      query: ({ examinerId, data }) =>
        api.speakingCertification.doCalculation({ examinerId }, data),
    }),

    saveSpeakingCert: builder.mutation<
      SpeakingCertificationDetailsResult,
      { examinerId: number; data: SpeakingCertificationCalculationDetails }
    >({
      query: ({ examinerId, data }) =>
        api.speakingCertification.saveCertification({ examinerId }, data),
      invalidatesTags: [
        'SpeakingCertification',
        'SpeakingCertificationList',
        'ExaminerStatus',
        'Examiner',
      ],
    }),

    deleteSpeakingCert: builder.mutation<File, { certificationId: number; examinerId: number }>({
      query: ({ certificationId, examinerId }) =>
        api.speakingCertification.delete({ id: certificationId, examinerId }),
      invalidatesTags: ['SpeakingCertificationList', 'ExaminerStatus', 'Examiner'],
    }),
  }),
});

export const {
  useSpeakingCertificationListQuery,
  useSpeakingCertificationDetailsQuery,
  useSpeakingNextCertDateQuery,
  useSpeakingCertRatesQuery,
  useAvailableSpeakingSetsQuery,
  useSpeakingIsAttemptsExceededQuery,
  useSpeakingIsGapExceededQuery,
  useSpeakingCertIsDeletableQuery,
  useSpeakingLastPassedCertDateQuery,
  useCalculateSpeakingCertMutation,
  useSaveSpeakingCertMutation,
  useDeleteSpeakingCertMutation,
} = speakingCertificationApi;
